import { useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { Button, Textarea } from '@/components/ui';

interface CustomPromptInputProps {
  disabled: boolean;
  onSubmit: (prompt: string) => void;
}

const MAX_PROMPT_CHARS = 10_000;

export function CustomPromptInput({ disabled, onSubmit }: CustomPromptInputProps) {
  const [value, setValue] = useState('');

  function send() {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    onSubmit(trimmed);
    setValue('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter (or any modifier) inserts a newline.
    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      send();
    }
  }

  const overLimit = value.length > MAX_PROMPT_CHARS;
  const sendDisabled = disabled || value.trim().length === 0 || overLimit;

  return (
    <div className="space-y-1">
      <div className="relative">
        <Textarea
          rows={3}
          placeholder={
            disabled
              ? 'Select a matter to enable Claude.'
              : 'Type a question for Claude. Enter to send, shift enter for newline.'
          }
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Custom prompt"
          invalid={overLimit}
        />
        <Button
          type="button"
          variant="primary"
          size="icon"
          aria-label="Send"
          className="absolute bottom-2 right-2 h-8 w-8"
          disabled={sendDisabled}
          onClick={send}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {overLimit ? (
        <p className="text-[10px] uppercase tracking-[0.2em] text-red-400">
          Prompt over {MAX_PROMPT_CHARS} characters
        </p>
      ) : null}
    </div>
  );
}
